import { GraduationCap, Sparkles, Shield, Key, Briefcase, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import badgePN from '@/assets/badge-policia-nacional.png';
import badgeGC from '@/assets/badge-guardia-civil.png';

export type OposicionesHeroVariant =
  | 'secretaria'
  | 'oposiciones_c1'
  | 'oposiciones_c2'
  | 'oposiciones_guardia_civil'
  | 'oposiciones_policia_nacional'
  | 'oposiciones_iipp';

interface VariantConfig {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  /** Tailwind gradient for background */
  bgGradient: string;
  /** Tailwind gradient for icon container */
  iconGradient: string;
  /** Tailwind gradient for title text */
  titleGradient: string;
  /** Color for subtitle */
  subtitleColor: string;
  /** Glow ring color (rgba via tailwind) */
  glowColor: string;
  badge?: string;
}

const VARIANTS: Record<OposicionesHeroVariant, VariantConfig> = {
  secretaria: {
    icon: GraduationCap,
    title: '¿Qué quieres estudiar hoy?',
    subtitle: 'Tu asistente inteligente para preparar oposiciones de administración local',
    bgGradient: 'from-violet-50 via-white to-purple-50',
    iconGradient: 'from-violet-600 to-purple-700',
    titleGradient: 'from-violet-700 via-purple-600 to-violet-700',
    subtitleColor: 'text-violet-600/70',
    glowColor: 'bg-violet-500/20',
  },
  oposiciones_c1: {
    icon: Briefcase,
    title: '¿Qué quieres estudiar hoy?',
    subtitle: 'Tu preparador para el Cuerpo General Administrativo del Estado (C1)',
    bgGradient: 'from-blue-50 via-white to-sky-50',
    iconGradient: 'from-blue-600 to-sky-700',
    titleGradient: 'from-blue-700 via-sky-600 to-blue-700',
    subtitleColor: 'text-blue-600/70',
    glowColor: 'bg-blue-500/20',
  },
  oposiciones_c2: {
    icon: FileText,
    title: '¿Qué quieres estudiar hoy?',
    subtitle: 'Tu preparador para Auxiliar Administrativo del Estado (C2)',
    bgGradient: 'from-orange-50 via-white to-amber-50',
    iconGradient: 'from-orange-500 to-amber-600',
    titleGradient: 'from-orange-600 via-amber-600 to-orange-600',
    subtitleColor: 'text-orange-600/70',
    glowColor: 'bg-orange-500/20',
  },
  oposiciones_guardia_civil: {
    icon: Shield,
    title: '¿Qué quieres estudiar hoy?',
    subtitle: 'Tu preparador para la Escala de Cabos y Guardias de la Guardia Civil',
    bgGradient: 'from-green-50 via-white to-emerald-50',
    iconGradient: 'from-green-700 to-emerald-800',
    titleGradient: 'from-green-700 via-emerald-700 to-green-700',
    subtitleColor: 'text-green-700/70',
    glowColor: 'bg-green-600/20',
    badge: badgeGC,
  },
  oposiciones_policia_nacional: {
    icon: Shield,
    title: '¿Qué quieres estudiar hoy?',
    subtitle: 'Tu preparador para la Escala Básica de la Policía Nacional',
    bgGradient: 'from-sky-50 via-white to-blue-50',
    iconGradient: 'from-sky-600 to-blue-700',
    titleGradient: 'from-sky-700 via-blue-600 to-sky-700',
    subtitleColor: 'text-sky-700/70',
    glowColor: 'bg-sky-500/20',
    badge: badgePN,
  },
  oposiciones_iipp: {
    icon: Key,
    title: '¿Qué quieres estudiar hoy?',
    subtitle: 'Tu preparador para Ayudante de Instituciones Penitenciarias',
    bgGradient: 'from-red-50 via-white to-rose-50',
    iconGradient: 'from-red-600 to-rose-700',
    titleGradient: 'from-red-700 via-rose-600 to-red-700',
    subtitleColor: 'text-red-600/70',
    glowColor: 'bg-red-500/20',
  },
};


interface HeroSectionOposicionesProps {
  variant?: OposicionesHeroVariant;
}

export function HeroSectionOposiciones({ variant = 'secretaria' }: HeroSectionOposicionesProps) {
  const cfg = VARIANTS[variant];
  const Icon = cfg.icon;

  return (
    <section className={`flex items-center justify-center min-h-[30vh] bg-gradient-to-br ${cfg.bgGradient} px-6`}>
      <div className="text-center max-w-2xl mx-auto">
        <div className="flex justify-center mb-4">
          <div className="relative">
            <div className={`absolute inset-0 ${cfg.glowColor} blur-xl rounded-full`} />
            <div className={`relative bg-gradient-to-br ${cfg.iconGradient} p-4 rounded-2xl shadow-lg`}>
              {cfg.badge ? (
                <img src={cfg.badge} alt="" className="h-10 w-10 object-contain" />
              ) : (
                <Icon className="h-10 w-10 text-white" />
              )}
            </div>
            <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-yellow-500" />
          </div>
        </div>

        <h1 className={`text-3xl md:text-4xl font-bold bg-gradient-to-r ${cfg.titleGradient} bg-clip-text text-transparent mb-3`}>
          {cfg.title}
        </h1>

        <p className={`text-base ${cfg.subtitleColor}`}>
          {cfg.subtitle}
        </p>
      </div>
    </section>
  );
}
